import { useState, useEffect } from "react";

const STYLES = `
.injection-warning-banner {
  background: #3a1212;
  border: 1px solid #a33;
  border-left: 4px solid #e44;
  color: #ffb4b4;
  padding: 12px 14px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 13px;
  line-height: 1.4;
}
.injection-warning-banner strong { color: #ff6b6b; display: block; margin-bottom: 4px; }

  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@300;400;500;600&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #080c14;
    --surface: #0d1520;
    --surface2: #111d2e;
    --border: #1e3048;
    --border2: #243a58;
    --text: #c8d8e8;
    --text-dim: #5a7a9a;
    --text-bright: #e8f4ff;
    --accent: #00c2ff;
    --accent-dim: #0077aa;
    --critical: #ff3d5a;
    --high: #ff7c2a;
    --medium: #ffd426;
    --low: #2aff9e;
    --mono: 'IBM Plex Mono', monospace;
    --sans: 'IBM Plex Sans', sans-serif;
  }

  body { background: var(--bg); color: var(--text); font-family: var(--sans); }

  .dashboard {
    min-height: 100vh;
    background: var(--bg);
    background-image: 
      radial-gradient(ellipse at 20% 0%, rgba(0,100,200,0.08) 0%, transparent 60%),
      radial-gradient(ellipse at 80% 100%, rgba(0,50,120,0.06) 0%, transparent 60%);
  }

  .header {
    border-bottom: 1px solid var(--border);
    padding: 0 32px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: rgba(13,21,32,0.95);
    backdrop-filter: blur(10px);
    position: sticky;
    top: 0;
    z-index: 100;
  }

  .logo {
    font-family: var(--mono);
    font-size: 13px;
    font-weight: 600;
    color: var(--accent);
    letter-spacing: 0.15em;
    text-transform: uppercase;
  }

  .logo span { color: var(--text-dim); }

  .header-right {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--low);
    box-shadow: 0 0 8px var(--low);
    animation: pulse 2s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }

  .header-stat {
    font-family: var(--mono);
    font-size: 11px;
    color: var(--text-dim);
  }

  .header-stat strong {
    color: var(--accent);
    font-weight: 600;
  }

  .signout-btn {
    background: transparent;
    border: 1px solid var(--border2);
    color: var(--text-dim);
    font-family: var(--mono);
    font-size: 10px;
    padding: 6px 14px;
    border-radius: 4px;
    cursor: pointer;
    letter-spacing: 0.05em;
    transition: all 0.2s;
  }
  .signout-btn:hover { border-color: var(--critical); color: var(--critical); }

  .main { padding: 32px; max-width: 1400px; margin: 0 auto; }

  .stats-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 32px;
  }

  .stat-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 20px 24px;
    position: relative;
    overflow: hidden;
  }

  .stat-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: var(--accent);
    opacity: 0.6;
  }

  .stat-card.critical::before { background: var(--critical); }
  .stat-card.savings::before { background: var(--low); }
  .stat-card.issues::before { background: var(--medium); }

  .stat-label {
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--text-dim);
    margin-bottom: 8px;
  }

  .stat-value {
    font-family: var(--mono);
    font-size: 28px;
    font-weight: 600;
    color: var(--text-bright);
    line-height: 1;
  }

  .stat-value.green { color: var(--low); }
  .stat-value.red { color: var(--critical); }

  .stat-sub {
    font-size: 11px;
    color: var(--text-dim);
    margin-top: 6px;
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .section-title {
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--text-dim);
  }

  .refresh-btn {
    background: transparent;
    border: 1px solid var(--border2);
    color: var(--text-dim);
    font-family: var(--mono);
    font-size: 11px;
    padding: 6px 14px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
    letter-spacing: 0.05em;
  }

  .refresh-btn:hover {
    border-color: var(--accent);
    color: var(--accent);
  }

  .cases-table {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    overflow: hidden;
  }

  .table-head {
    display: grid;
    grid-template-columns: 2fr 1.2fr 0.8fr 1fr 1.2fr 1fr 1.4fr;
    padding: 12px 20px;
    background: var(--surface2);
    border-bottom: 1px solid var(--border);
  }

  .th {
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--text-dim);
  }

  .case-row {
    display: grid;
    grid-template-columns: 2fr 1.2fr 0.8fr 1fr 1.2fr 1fr 1.4fr;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border);
    align-items: center;
    cursor: pointer;
    transition: background 0.15s;
  }

  .case-row:hover { background: var(--surface2); }
  .case-row:last-child { border-bottom: none; }

  .patient-name {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-bright);
  }

  .patient-id {
    font-family: var(--mono);
    font-size: 10px;
    color: var(--text-dim);
    margin-top: 2px;
  }

  .cell {
    font-family: var(--mono);
    font-size: 12px;
    color: var(--text);
  }

  .risk-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-family: var(--mono);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.08em;
    padding: 3px 8px;
    border-radius: 3px;
  }

  .risk-badge.CRITICAL { background: rgba(255,61,90,0.15); color: var(--critical); border: 1px solid rgba(255,61,90,0.3); }
  .risk-badge.HIGH { background: rgba(255,124,42,0.15); color: var(--high); border: 1px solid rgba(255,124,42,0.3); }
  .risk-badge.MEDIUM { background: rgba(255,212,38,0.15); color: var(--medium); border: 1px solid rgba(255,212,38,0.3); }
  .risk-badge.LOW { background: rgba(42,255,158,0.15); color: var(--low); border: 1px solid rgba(42,255,158,0.3); }
  .risk-badge.UNKNOWN { background: rgba(90,122,154,0.15); color: var(--text-dim); border: 1px solid var(--border); }

  .savings-cell {
    font-family: var(--mono);
    font-size: 12px;
    color: var(--low);
    font-weight: 500;
  }

  .status-select {
    background: var(--surface2);
    border: 1px solid var(--border2);
    color: var(--text);
    font-family: var(--mono);
    font-size: 10px;
    padding: 4px 8px;
    border-radius: 4px;
    cursor: pointer;
    outline: none;
    width: 100%;
  }

  .status-select:focus { border-color: var(--accent); }
  .status-select.waiting { border-color: var(--medium); color: var(--medium); }
  .status-select.resolved { border-color: var(--low); color: var(--low); }

  .info-request-box {
    margin-top: 6px;
    width: 100%;
  }

  .info-request-textarea {
    width: 100%;
    background: var(--surface2);
    border: 1px solid var(--medium);
    color: var(--text);
    font-family: var(--mono);
    font-size: 10px;
    padding: 6px 8px;
    border-radius: 4px;
    resize: vertical;
    min-height: 60px;
    outline: none;
    line-height: 1.5;
  }

  .info-request-send {
    margin-top: 4px;
    width: 100%;
    background: var(--medium);
    color: #080c14;
    border: none;
    font-family: var(--mono);
    font-size: 10px;
    font-weight: 600;
    padding: 5px 8px;
    border-radius: 4px;
    cursor: pointer;
    letter-spacing: 0.05em;
  }

  .info-request-send:hover { opacity: 0.85; }
  .info-request-send:disabled { opacity: 0.5; cursor: not-allowed; }

  .send-success {
    font-family: var(--mono);
    font-size: 10px;
    color: var(--low);
    margin-top: 4px;
  }

  .detail-overlay {
    position: fixed;
    inset: 0;
    background: rgba(8,12,20,0.85);
    backdrop-filter: blur(4px);
    z-index: 200;
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
    padding: 16px;
  }

  .detail-panel {
    width: 560px;
    height: calc(100vh - 32px);
    background: var(--surface);
    border: 1px solid var(--border2);
    border-radius: 12px;
    overflow-y: auto;
    animation: slideIn 0.2s ease-out;
  }

  @keyframes slideIn {
    from { transform: translateX(20px); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }

  .detail-header {
    padding: 24px;
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    position: sticky;
    top: 0;
    background: var(--surface);
    z-index: 10;
  }

  .detail-name {
    font-size: 20px;
    font-weight: 600;
    color: var(--text-bright);
    margin-bottom: 4px;
  }

  .detail-meta {
    font-family: var(--mono);
    font-size: 11px;
    color: var(--text-dim);
  }

  .close-btn {
    background: transparent;
    border: 1px solid var(--border2);
    color: var(--text-dim);
    width: 32px;
    height: 32px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
    flex-shrink: 0;
  }

  .close-btn:hover { border-color: var(--critical); color: var(--critical); }

  .detail-body { padding: 24px; }

  .detail-summary {
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 16px;
    font-size: 13px;
    line-height: 1.6;
    color: var(--text);
    margin-bottom: 24px;
  }

  .detail-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 24px;
  }

  .detail-stat {
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 12px;
    text-align: center;
  }

  .detail-stat-val {
    font-family: var(--mono);
    font-size: 18px;
    font-weight: 600;
    color: var(--text-bright);
  }

  .detail-stat-val.green { color: var(--low); }

  .detail-stat-label {
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--text-dim);
    margin-top: 4px;
  }

  .issues-title {
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--text-dim);
    margin-bottom: 12px;
    margin-top: 24px;
  }

  .issue-card {
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 14px;
    margin-bottom: 10px;
    border-left: 3px solid var(--border2);
  }

  .issue-card.CRITICAL { border-left-color: var(--critical); }
  .issue-card.HIGH { border-left-color: var(--high); }
  .issue-card.MEDIUM { border-left-color: var(--medium); }
  .issue-card.LOW { border-left-color: var(--low); }

  .issue-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 6px;
    gap: 12px;
  }

  .issue-line {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-bright);
    flex: 1;
  }

  .issue-savings {
    font-family: var(--mono);
    font-size: 12px;
    color: var(--low);
    font-weight: 600;
    white-space: nowrap;
  }

  .issue-desc {
    font-size: 12px;
    color: var(--text);
    line-height: 1.5;
    margin-bottom: 6px;
  }

  .issue-action {
    font-family: var(--mono);
    font-size: 11px;
    color: var(--accent);
    margin-top: 4px;
  }

  .actions-section { margin-top: 8px; }

  .action-card {
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 14px;
    margin-bottom: 10px;
  }

  .action-priority {
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.12em;
    color: var(--accent);
    margin-bottom: 6px;
  }

  .action-text {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-bright);
    margin-bottom: 8px;
  }

  .action-script {
    font-family: var(--mono);
    font-size: 11px;
    color: var(--text-dim);
    line-height: 1.5;
    background: var(--bg);
    padding: 8px;
    border-radius: 4px;
    margin-top: 6px;
  }

  .loading-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 48px;
    color: var(--text-dim);
    font-family: var(--mono);
    font-size: 12px;
  }

  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid var(--border2);
    border-top-color: var(--accent);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px;
    gap: 12px;
  }

  .empty-icon { font-size: 32px; }

  .empty-title {
    font-family: var(--mono);
    font-size: 13px;
    color: var(--text-dim);
  }

  .empty-sub {
    font-size: 12px;
    color: var(--text-dim);
    opacity: 0.6;
  }
`;

const API_URL = "https://rqrjxi4dtzj46lvmd4svxh7ixa0mafny.lambda-url.us-east-2.on.aws/";
const STATUS_UPDATE_URL = "https://rqrjxi4dtzj46lvmd4svxh7ixa0mafny.lambda-url.us-east-2.on.aws/status";

const STATUS_OPTIONS = [
  "NEW",
  "WAITING FOR PATIENT INFO",
  "UNDER REVIEW",
  "REQUESTING RECORDS",
  "NEGOTIATING",
  "RESOLVED",
  "PAID",
  "CLOSED",
];

function caseAge(str) {
  if (!str) return "";
  try {
    const d = new Date(str.replace(/_/g, "T").replace(/T(\d{2})-(\d{2})-(\d{2})$/, "T$1:$2:$3") + "Z");
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days === 0) return "today";
    if (days === 1) return "1 day ago";
    return `${days}d ago`;
  } catch { return ""; }
}

function formatDate(str) {
  if (!str) return "—";
  const d = str.replace("_", " ").replace(/-(\d{2})-(\d{2})-(\d{2})$/, " $1:$2:$3");
  return d.slice(0, 16);
}

function parseSavings(str) {
  if (!str) return 0;
  const nums = str.match(/[\d,]+(?:\.\d+)?/g);
  if (!nums) return 0;
  const vals = nums.map(n => parseFloat(n.replace(/,/g, "")));
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

// ── Status cell with info-request box ─────────────────────────────────────
function StatusCell({ caseData, status, onStatusChange, adminToken }) {
  const [infoNeeded, setInfoNeeded] = useState("");
  const [sending, setSending]       = useState(false);
  const [sent, setSent]             = useState(false);
  const isWaiting = status === "WAITING FOR PATIENT INFO";

  const handleStatusChange = (val) => {
    onStatusChange(caseData.patient_id, val);
    setSent(false);
  };

  const sendInfoRequest = async () => {
    if (!infoNeeded.trim()) return;
    setSending(true);
    try {
      // Save status + note to S3 via PBAGCasesAPI (or directly)
      // Email via SES — wired when SES is approved
      // For now: save to S3 as status.json
      await fetch(`https://rqrjxi4dtzj46lvmd4svxh7ixa0mafny.lambda-url.us-east-2.on.aws/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${adminToken}` },
        body: JSON.stringify({
          action: "update_status",
          patientId: caseData.patient_id,
          status: "WAITING FOR PATIENT INFO",
          note: infoNeeded,
          patientEmail: caseData.email || "",
          patientName: caseData.patient_name || "",
        }),
      });
      setSent(true);
      setInfoNeeded("");
    } catch (e) {
      console.error("Status update failed:", e);
    }
    setSending(false);
  };

  return (
    <div onClick={e => e.stopPropagation()}>
      <select
        className={`status-select${isWaiting ? " waiting" : status === "RESOLVED" || status === "PAID" ? " resolved" : ""}`}
        value={status}
        onChange={e => handleStatusChange(e.target.value)}
      >
        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
      </select>

      {isWaiting && (
        <div className="info-request-box">
          <textarea
            className="info-request-textarea"
            placeholder="What do you need from the patient? (they'll receive this in an email)"
            value={infoNeeded}
            onChange={e => setInfoNeeded(e.target.value)}
          />
          <button
            className="info-request-send"
            onClick={sendInfoRequest}
            disabled={sending || !infoNeeded.trim()}
          >
            {sending ? "SENDING..." : "SEND REQUEST →"}
          </button>
          {sent && <div className="send-success">✓ Saved — email will send when SES is approved</div>}
        </div>
      )}
    </div>
  );
}

// ── Advocate Tools Panel ──────────────────────────────────────────────────
const CASES_API = "https://rqrjxi4dtzj46lvmd4svxh7ixa0mafny.lambda-url.us-east-2.on.aws/";
const UPLOAD_HANDLER = "https://b6l6d5cw5e5m2dssmvn7ngtfvm0raoqj.lambda-url.us-east-2.on.aws/";

function AdvocatePanel({ caseData, notes, setNotes, savingNote, setSavingNote, uploadedFiles, setUploadedFiles, uploadingFile, setUploadingFile, currentStatus, adminToken }) {
  const patientId    = caseData.patient_id;
  const noteText     = notes[patientId] || caseData.advocate_notes || "";
  const isRequesting = currentStatus === "REQUESTING RECORDS";

  const saveNote = async () => {
    setSavingNote(prev => ({ ...prev, [patientId]: true }));
    try {
      await fetch(CASES_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${adminToken}` },
        body: JSON.stringify({
          action: "update_status",
          patientId,
          status: currentStatus,
          advocate_notes: noteText,
          patientEmail: caseData.email || "",
          patientName: caseData.patient_name || "",
        }),
      });
    } catch (e) { console.error("Save note failed:", e); }
    setSavingNote(prev => ({ ...prev, [patientId]: false }));
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploadingFile(true);
    try {
      // Request presigned URL from UploadHandler — use patient folder directly
      const res = await fetch(UPLOAD_HANDLER, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${adminToken}` },
        body: JSON.stringify({
          filename: file.name,
          contentType: file.type || "application/octet-stream",
          fileSize: file.size,
          patientId: patientId,
          isAdminUpload: true,
        }),
      });
      const data = await res.json();
      if (!data.uploadUrl) { alert("Upload failed: " + (data.error || "Unknown error")); setUploadingFile(false); return; }

      await fetch(data.uploadUrl, { method: "PUT", body: file, headers: { "Content-Type": file.type || "application/octet-stream" } });
      setUploadedFiles(prev => ({ ...prev, [patientId]: [...(prev[patientId] || []), file.name] }));
    } catch (e) { console.error("File upload failed:", e); }
    setUploadingFile(false);
  };

  return (
    <div style={{ marginTop: "24px", borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "20px" }}>
      <div style={{ fontFamily: "monospace", fontSize: "11px", color: "#888", letterSpacing: "0.1em", marginBottom: "14px" }}>// Advocate Tools</div>

      {/* Requesting Records note */}
      {isRequesting && (
        <div style={{ marginBottom: "16px", background: "rgba(255,193,7,0.08)", border: "1px solid rgba(255,193,7,0.2)", borderRadius: "8px", padding: "14px" }}>
          <div style={{ fontSize: "11px", color: "#f0ad4e", marginBottom: "8px", fontFamily: "monospace", letterSpacing: "0.08em" }}>REQUESTING RECORDS — NOTES</div>
          <textarea
            placeholder="What records are you requesting? From whom? Any reference numbers..."
            value={noteText}
            onChange={e => setNotes(prev => ({ ...prev, [patientId]: e.target.value }))}
            style={{ width: "100%", minHeight: "80px", padding: "10px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "6px", color: "#e6edf3", fontSize: "13px", fontFamily: "sans-serif", resize: "vertical", boxSizing: "border-box" }}
          />
          <button onClick={saveNote} disabled={savingNote[patientId]}
            style={{ marginTop: "8px", padding: "8px 16px", background: "#f0ad4e", color: "#0d1117", border: "none", borderRadius: "6px", fontSize: "11px", fontWeight: 700, fontFamily: "monospace", cursor: "pointer" }}>
            {savingNote[patientId] ? "SAVING..." : "SAVE NOTES →"}
          </button>
        </div>
      )}

      {/* Advocate notes */}
      <div style={{ marginBottom: "16px" }}>
        <div style={{ fontSize: "11px", color: "#888", marginBottom: "8px", fontFamily: "monospace", letterSpacing: "0.08em" }}>ADVOCATE NOTES (patient-facing PDF)</div>
        <textarea
          placeholder="Notes about this case — what you found, what was negotiated, next steps. This will appear in the patient's downloadable report."
          value={noteText}
          onChange={e => setNotes(prev => ({ ...prev, [patientId]: e.target.value }))}
          style={{ width: "100%", minHeight: "100px", padding: "10px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "6px", color: "#e6edf3", fontSize: "13px", fontFamily: "sans-serif", resize: "vertical", boxSizing: "border-box" }}
        />
        <button onClick={saveNote} disabled={savingNote[patientId]}
          style={{ marginTop: "8px", padding: "8px 16px", background: "var(--accent)", color: "#0d1117", border: "none", borderRadius: "6px", fontSize: "11px", fontWeight: 700, fontFamily: "monospace", cursor: "pointer" }}>
          {savingNote[patientId] ? "SAVING..." : "SAVE NOTES →"}
        </button>
      </div>

      {/* File upload */}
      <div>
        <div style={{ fontSize: "11px", color: "#888", marginBottom: "8px", fontFamily: "monospace", letterSpacing: "0.08em" }}>INTERNAL FILES (not visible to patient)</div>
        <label style={{ display: "inline-block", padding: "8px 16px", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "6px", fontSize: "11px", fontFamily: "monospace", color: "#e6edf3", cursor: uploadingFile ? "not-allowed" : "pointer", letterSpacing: "0.08em" }}>
          {uploadingFile ? "UPLOADING..." : "📎 ATTACH FILE"}
          <input type="file" style={{ display: "none" }} disabled={uploadingFile}
            onChange={e => e.target.files[0] && uploadFile(e.target.files[0])} />
        </label>
        {uploadedFiles[patientId]?.length > 0 && (
          <div style={{ marginTop: "8px" }}>
            {uploadedFiles[patientId].map((f, i) => (
              <div key={i} style={{ fontSize: "11px", color: "var(--accent)", fontFamily: "monospace", padding: "2px 0" }}>✓ {f}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Settlement Panel ──────────────────────────────────────────────────────
const STRIPE_LAMBDA = "https://aj2ur7yqqpktomxm3yb25mpc340duifs.lambda-url.us-east-2.on.aws/";

function SettlementPanel({ caseData, settlement, setSettlement, onClose, adminToken }) {
  const set = (k, v) => setSettlement(s => ({ ...s, [k]: v }));

  const original   = parseFloat((settlement.originalBill   || "0").replace(/[^0-9.]/g, "")) || 0;
  const negotiated = parseFloat((settlement.negotiatedAmount || "0").replace(/[^0-9.]/g, "")) || 0;
  const savings    = Math.max(0, original - negotiated);
  const fee        = savings * 0.20;

  const fmt = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

  const charge = async () => {
    if (!original || !negotiated) { set("error", "Please enter both amounts."); return; }
    if (negotiated >= original)   { set("error", "Negotiated amount must be less than original."); return; }
    if (!caseData.stripe_customer_id) { set("error", "No payment method on file for this patient."); return; }

    set("charging", true); set("error", "");
    try {
      const res  = await fetch(STRIPE_LAMBDA, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${adminToken}` },
        body: JSON.stringify({
          action: "charge_settlement",
          customerId: caseData.stripe_customer_id,
          amount: Math.round(fee * 100), // cents
          patientName: caseData.patient_name || caseData.patient_id,
          patientEmail: caseData.email || "",
          originalBill: original,
          negotiatedAmount: negotiated,
          savings: savings,
          fee: fee,
          caseId: caseData.patient_id,
        }),
      });
      const data = await res.json();
      const parsed = typeof data.body === "string" ? JSON.parse(data.body) : data;
      if (!parsed.success) { set("error", parsed.error || "Charge failed."); set("charging", false); return; }
      set("charged", true); set("charging", false);
    } catch (e) {
      set("error", "Something went wrong. Please try again.");
      set("charging", false);
    }
  };

  if (settlement.charged) return (
    <div style={{ marginTop: "24px", background: "#f0faf4", border: "1px solid #b8e8cb", borderRadius: "10px", padding: "20px", textAlign: "center" }}>
      <div style={{ fontSize: "24px", marginBottom: "8px" }}>✓</div>
      <div style={{ fontWeight: 600, color: "#1e8449", marginBottom: "4px" }}>Payment collected successfully</div>
      <div style={{ fontSize: "13px", color: "#555" }}>Receipt sent to patient. Case is now PAID.</div>
    </div>
  );

  return (
    <div style={{ marginTop: "24px", borderTop: "1px solid #eee", paddingTop: "20px" }}>
      <div style={{ fontFamily: "monospace", fontSize: "11px", color: "#888", letterSpacing: "0.1em", marginBottom: "14px" }}>// Settlement & Charge</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" }}>
        <div>
          <label style={{ display: "block", fontSize: "11px", color: "#888", marginBottom: "4px" }}>ORIGINAL BILL AMOUNT</label>
          <input
            type="text" placeholder="e.g. 38814.38"
            value={settlement.originalBill}
            onChange={e => set("originalBill", e.target.value)}
            style={{ width: "100%", padding: "10px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "14px", boxSizing: "border-box" }}
          />
        </div>
        <div>
          <label style={{ display: "block", fontSize: "11px", color: "#888", marginBottom: "4px" }}>NEGOTIATED AMOUNT</label>
          <input
            type="text" placeholder="e.g. 29000.00"
            value={settlement.negotiatedAmount}
            onChange={e => set("negotiatedAmount", e.target.value)}
            style={{ width: "100%", padding: "10px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "14px", boxSizing: "border-box" }}
          />
        </div>
      </div>

      {original > 0 && negotiated > 0 && negotiated < original && (
        <div style={{ background: "#f8f9fa", borderRadius: "8px", padding: "14px", marginBottom: "16px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", textAlign: "center" }}>
          <div>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "#1e8449" }}>{fmt(savings)}</div>
            <div style={{ fontSize: "10px", color: "#888", marginTop: "2px" }}>SAVINGS</div>
          </div>
          <div>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "#1a3a5c" }}>{fmt(fee)}</div>
            <div style={{ fontSize: "10px", color: "#888", marginTop: "2px" }}>EXHALE FEE (20%)</div>
          </div>
          <div>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "#1a1a1a" }}>{fmt(negotiated)}</div>
            <div style={{ fontSize: "10px", color: "#888", marginTop: "2px" }}>PATIENT PAYS HOSPITAL</div>
          </div>
        </div>
      )}

      {settlement.error && (
        <div style={{ color: "#c0392b", fontSize: "13px", marginBottom: "12px" }}>{settlement.error}</div>
      )}

      {!caseData.stripe_customer_id && (
        <div style={{ color: "#e67e22", fontSize: "13px", marginBottom: "12px" }}>⚠ No payment method on file — patient may be a promo user.</div>
      )}

      <button
        onClick={charge}
        disabled={settlement.charging || !original || !negotiated || negotiated >= original}
        style={{
          width: "100%", padding: "14px", background: settlement.charging ? "#888" : "#1a3a5c",
          color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: 600,
          cursor: settlement.charging ? "not-allowed" : "pointer", letterSpacing: "0.05em"
        }}
      >
        {settlement.charging ? "PROCESSING..." : `CHARGE ${fmt(fee)} →`}
      </button>
    </div>
  );
}

export default function ExhaleDashboard({ adminToken, onLogout }) {
  const [cases, setCases]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [selected, setSelected]   = useState(null);
  const [settlement, setSettlement] = useState({ originalBill: "", negotiatedAmount: "", charging: false, charged: false, error: "" });
  const [activeTab, setActiveTab]   = useState("active"); // "active" | "completed"
  const [search, setSearch]         = useState("");
  const [notes, setNotes]           = useState({}); // { patient_id: noteText }
  const [savingNote, setSavingNote] = useState({});
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({}); // { patient_id: [filename] }
  const [statuses, setStatuses]   = useState({});
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchCases = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_URL, { headers: { "Authorization": `Bearer ${adminToken}` } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const arr = Array.isArray(data) ? data : [];
      setCases(arr);
      // Load saved statuses
      const saved = {};
      arr.forEach(c => { if (c.case_status) saved[c.patient_id] = c.case_status; });
      setStatuses(prev => ({ ...saved, ...prev }));
      setLastRefresh(new Date().toLocaleTimeString());
    } catch (err) {
      setError(`Could not load cases: ${err.message}`);
      setCases([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchCases(); }, []);

  const totalSavings   = cases.reduce((sum, c) => sum + parseSavings(c.estimated_savings), 0);
  const highRiskCount  = cases.filter(c => c.risk_level === "CRITICAL" || c.risk_level === "HIGH").length;
  const totalIssues    = cases.reduce((sum, c) => sum + (c.total_issues_found || 0), 0);

  const COMPLETED_STATUSES = ["PAID", "CLOSED"];
  const activeCases    = cases.filter(c => !COMPLETED_STATUSES.includes(statuses[c.patient_id] || c.case_status || "NEW"));
  const completedCases = cases.filter(c =>  COMPLETED_STATUSES.includes(statuses[c.patient_id] || c.case_status || "NEW"));
  const displayCases   = (activeTab === "active" ? activeCases : completedCases)
    .filter(c => !search || (c.patient_name || "").toLowerCase().includes(search.toLowerCase()) || (c.patient_id || "").toLowerCase().includes(search.toLowerCase()));

  const updateStatus = (id, val) => {
    setStatuses(prev => ({ ...prev, [id]: val }));
  };

  return (
    <>
      <style>{STYLES}</style>
      <div className="dashboard">
        <header className="header">
          <div className="logo">EXHALE <span>// Case Management</span></div>
          <div className="header-right">
            <div className="status-dot" />
            <span className="header-stat"><strong>{cases.length}</strong> active cases</span>
            {lastRefresh && <span className="header-stat">refreshed <strong>{lastRefresh}</strong></span>}
            {onLogout && (
              <button className="signout-btn" onClick={onLogout}>SIGN OUT</button>
            )}
          </div>
        </header>

        <main className="main">
          <div className="stats-row">
            <div className="stat-card">
              <div className="stat-label">Total Cases</div>
              <div className="stat-value">{cases.length}</div>
              <div className="stat-sub">patients on file</div>
            </div>
            <div className="stat-card savings">
              <div className="stat-label">Avg Potential Savings</div>
              <div className="stat-value green">${Math.round(totalSavings / Math.max(cases.length, 1)).toLocaleString()}</div>
              <div className="stat-sub">per case</div>
            </div>
            <div className="stat-card issues">
              <div className="stat-label">Total Issues Found</div>
              <div className="stat-value">{totalIssues}</div>
              <div className="stat-sub">across all cases</div>
            </div>
            <div className="stat-card critical">
              <div className="stat-label">High/Critical Risk</div>
              <div className="stat-value red">{highRiskCount}</div>
              <div className="stat-sub">need immediate action</div>
            </div>
          </div>

          <div className="section-header">
            <div className="section-title">// Patient Cases</div>
            <button className="refresh-btn" onClick={fetchCases}>↻ Refresh</button>
          </div>

          {/* Search + Tabs */}
          <div style={{ display: "flex", gap: "8px", marginBottom: "16px", alignItems: "center" }}>
            <input
              type="text" placeholder="Search patients..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ padding: "8px 14px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "6px", color: "#e6edf3", fontFamily: "monospace", fontSize: "11px", width: "200px", outline: "none" }}
            />
            <div style={{ flex: 1 }} />
          </div>
          {/* Tabs */}
          <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
            {[["active", `Active (${activeCases.length})`], ["completed", `Completed (${completedCases.length})`]].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                style={{
                  padding: "8px 20px", border: "none", borderRadius: "6px", cursor: "pointer",
                  fontFamily: "monospace", fontSize: "11px", letterSpacing: "0.1em", fontWeight: 600,
                  background: activeTab === key ? "var(--accent)" : "rgba(255,255,255,0.05)",
                  color: activeTab === key ? "#0d1117" : "var(--text-dim)",
                }}
              >{label.toUpperCase()}</button>
            ))}
          </div>

          <div className="cases-table">
            <div className="table-head">
              <div className="th">Patient</div>
              <div className="th">Age</div>
              <div className="th">Analyzed</div>
              <div className="th">Risk</div>
              <div className="th">Issues</div>
              <div className="th">Savings</div>
              <div className="th">Billed</div>
              <div className="th">Status</div>
            </div>

            {loading ? (
              <div className="loading-row">
                <div className="spinner" />
                Loading cases from S3...
              </div>
            ) : error ? (
              <div className="empty-state">
                <div className="empty-icon">⚠</div>
                <div className="empty-title">{error}</div>
              </div>
            ) : cases.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📂</div>
                <div className="empty-title">No cases found</div>
                <div className="empty-sub">Upload a bill to mbr-bills/incoming/ to get started</div>
              </div>
            ) : (
              displayCases.map(c => (
                <div key={c.patient_id} className="case-row" onClick={() => setSelected(c)}>
                  <div>
                    <div className="patient-name">{c.patient_name || c.patient_id}</div>
                    <div className="patient-id">{c.patient_id}</div>
                  </div>
                  <div className="cell" style={{ color: "var(--text-dim)", fontSize: "11px" }}>{caseAge(c.analyzed_at)}</div>
                  <div className="cell">{formatDate(c.analyzed_at)}</div>
                  <div>
                    <span className={`risk-badge ${c.risk_level || "UNKNOWN"}`}>
                      {c.risk_level || "—"}
                    </span>
                  </div>
                  <div className="cell">{c.total_issues_found ?? "—"}</div>
                  <div className="savings-cell">{c.estimated_savings || "—"}</div>
                  <div className="cell">{c.total_billed || "—"}</div>
                  <div>
                    <StatusCell
                      adminToken={adminToken}
                      caseData={c}
                      status={statuses[c.patient_id] || "NEW"}
                      onStatusChange={updateStatus}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </main>

        {selected && (
          <div className="detail-overlay" onClick={() => setSelected(null)}>
            <div className="detail-panel" onClick={e => e.stopPropagation()}>
              <div className="detail-header">
                <div>
                  <div className="detail-name">{selected.patient_name || selected.patient_id}</div>
                  <div className="detail-meta">
                    {selected.patient_id} · {selected.source_file} · {formatDate(selected.analyzed_at)} · {caseAge(selected.analyzed_at)}
                  </div>
                  {(selected.phone || selected.email) && (
                    <div style={{ marginTop: "6px", fontSize: "12px", color: "var(--accent)", fontFamily: "monospace" }}>
                      {selected.phone && <span>📞 {selected.phone} &nbsp;</span>}
                      {selected.email && <span>✉ {selected.email}</span>}
                    </div>
                  )}
                </div>
                <button className="close-btn" onClick={() => setSelected(null)}>✕</button>
              </div>

              <div className="detail-body">
                {(selected._security_flag || selected.injection_detected) && (
                  <div className="injection-warning-banner">
                    <strong>⚠️ SECURITY WARNING — Review the source bill manually</strong>
                    <div>
                      This upload attempted to inject instructions into the AI analysis.
                      The AI output for this case may be unreliable. Open the original uploaded
                      document and verify every figure against it before trusting the analysis,
                      updating status, or preparing any charge.
                    </div>
                  </div>
                )}
                <div className="detail-stats">
                  <div className="detail-stat">
                    <div className="detail-stat-val">
                      <span className={`risk-badge ${selected.risk_level || "UNKNOWN"}`}>{selected.risk_level}</span>
                    </div>
                    <div className="detail-stat-label">Risk Level</div>
                  </div>
                  <div className="detail-stat">
                    <div className="detail-stat-val green">{selected.estimated_savings || "—"}</div>
                    <div className="detail-stat-label">Est. Savings</div>
                  </div>
                  <div className="detail-stat">
                    <div className="detail-stat-val">{selected.total_issues_found ?? "—"}</div>
                    <div className="detail-stat-label">Issues Found</div>
                  </div>
                </div>

                {selected.summary && (
                  <div className="detail-summary">{selected.summary}</div>
                )}

                {selected.issues?.length > 0 && (
                  <>
                    <div className="issues-title">// Billing Issues</div>
                    {selected.issues.map(issue => (
                      <div key={issue.issue_id} className={`issue-card ${issue.severity}`}>
                        <div className="issue-top">
                          <div className="issue-line">{issue.line_item}</div>
                          {issue.potential_savings && (
                            <div className="issue-savings">{issue.potential_savings}</div>
                          )}
                        </div>
                        <div className="issue-desc">{issue.description}</div>
                        {issue.action_required && (
                          <div className="issue-action">→ {issue.action_required}</div>
                        )}
                      </div>
                    ))}
                  </>
                )}

                {selected.recommended_actions?.length > 0 && (
                  <div className="actions-section">
                    <div className="issues-title">// Recommended Actions</div>
                    {selected.recommended_actions.map((action, i) => (
                      <div key={i} className="action-card">
                        <div className="action-priority">PRIORITY {action.priority}</div>
                        <div className="action-text">{action.action}</div>
                        {action.contact && <div className="issue-action">📞 {action.contact}</div>}
                        {action.script && <div className="action-script">"{action.script}"</div>}
                        {action.deadline && <div className="issue-action">⏰ {action.deadline}</div>}
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Advocate Tools ── */}
                <AdvocatePanel
                  adminToken={adminToken}
                  caseData={selected}
                  notes={notes}
                  setNotes={setNotes}
                  savingNote={savingNote}
                  setSavingNote={setSavingNote}
                  uploadedFiles={uploadedFiles}
                  setUploadedFiles={setUploadedFiles}
                  uploadingFile={uploadingFile}
                  setUploadingFile={setUploadingFile}
                  currentStatus={statuses[selected.patient_id] || "NEW"}
                />

                {/* ── Settlement / Charge Section ── */}
                <SettlementPanel
                  adminToken={adminToken}
                  caseData={selected}
                  settlement={settlement}
                  setSettlement={setSettlement}
                  onClose={() => setSelected(null)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
